/* eslint-disable promise/catch-or-return */
import context from '@/context'
import { processUserReport, getCellMetadataFromReportSubmissionData, mergeSourceData, mergeMetadata } from '@/store/utils/reportUtils'
import getComponentKey from '@/utils/getComponentKey'
import { ReportSubmissionState } from '@/enums/ReportSubmissionState'
import { allowErrorDialog } from '@/utils/fetch'
import { openErrorDialog } from '@/utils/dialogHelpers'

const initialState = {
  fetching: false,
  reloading: false,
  fetchingId: null,
  adding: false,
  removing: false,
  attachmentsKey: getComponentKey(),
  reportSubmission: null,
  reportSubmissionState: null,
  sourceHeaders: null,
  sourceRowHeaders: null,
  reportSubmissionWithGroup: null,
  sourceDataUpdating: false,
  sourceDataRecalculating: false,
  sourceDataSaving: false,
  sourceDataSavingError: false,
  sourceData: [],
  cellMetadata: [],
  reportKey: 0,
  scrollToRow: 0,
  rowDataTypes: {},
  colDataTypes: {},
  bankDefResidentCodes: {},
  bankDefNonResidentCodes: {},
  bankDefCodes: {},
  branchDefCodes: {},
  userDefCodes: {},
  key: 0,
  userRowsUpdating: false,
  individualCellsRecalculating: false,
  retryingCellRecalculation: false,
  changesToPersist: [],
  persistTimeoutId: null,
  recalcQueue: [],
  recalcTimeoutId: null,
  activeEditor: true,
  readOnly: false
}

export const actionTypes = {
  ReportSubmissionSetComment: 'reportSubmission/setComment',
  ReportSubmissionFetching: 'reportSubmission/fetching',
  ReportSubmissionFetched: 'reportSubmission/fetched',
  ReportSubmissionWithGroupFetching: 'reportSubmission/withGroup/fetching',
  ReportSubmissionWithGroupFetched: 'reportSubmission/withGroup/fetched',
  ReportSubmissionAttachmentAdding: 'reportSubmission/attachment/adding',
  ReportSubmissionAttachmentAdded: 'reportSubmission/attachment/added',
  ReportSubmissionAttachmentRemoving: 'reportSubmission/attachment/removing',
  ReportSubmissionAttachmentRemoved: 'reportSubmission/attachment/removed',
  ReportSubmissionStatusUpdating: 'reportSubmission/status/updating',
  ReportSubmissionStatusUpdated: 'reportSubmission/status/updated',
  ReportSubmissionDataUpdating: 'reportSubmission/data/updating',
  ReportSubmissionDataUpdated: 'reportSubmission/data/updated',
  ReportSubmissionGroupStatusUpdated: 'reportSubmission/group/status/updated',
  ReportSubmissionError: 'reportSubmission/error',
  ReportSubmissonTableIsReadOnly: 'reportSubmisson/setReadOnlyMode',

  UserRowsUpdating: 'userRows/updating',
  UserRowsUpdated: 'userRows/updated',
  UserRowsCreate: 'userRows/create',
  UserRowsUpdate: 'userRows/update',
  UserRowsDelete: 'userRows/delete'
}

const unfinishedStateSet = (getState, dispatch) => {
  const state = getState()

  if (state.reportSubmissions.reportSubmissionState === ReportSubmissionState.enum.Unbegun ||
    state.reportSubmissions.reportSubmissionState === ReportSubmissionState.enum.Rejected) {
    dispatch({ type: actionTypes.ReportSubmissionStatusUpdated, data: ReportSubmissionState.enum.Unfinished })
  }
}

const receivedReport = (response, dispatch) => {
  const finalizedGrid = processUserReport(response)
  const sourceData = response.reportSubmissionCells

  const cellMetadata = getCellMetadataFromReportSubmissionData(response.reportSubmissionCells)

  dispatch({ type: actionTypes.ReportSubmissionFetched, data: finalizedGrid })
  dispatch({ type: actionTypes.ReportSubmissionDataUpdated, data: { cellMetadata: cellMetadata, data: sourceData } })
}

const getInternal = (dispatch, getState, reportId, groupId) => {
  dispatch({ type: actionTypes.ReportSubmissionFetching, data: reportId })
  dispatch({ type: actionTypes.ReportSubmissionDataUpdating })

  const err = () => dispatch({ type: actionTypes.ReportSubmissionError, update: { fetching: false, fetchingId: null } })

  if (!groupId) context.api.reportSubmissionService.get(reportId, allowErrorDialog).then(resp => receivedReport(resp, dispatch)).catch(err)
  else context.api.reportSubmissionService.getByGroup(groupId, reportId, allowErrorDialog).then(resp => receivedReport(resp, dispatch)).catch(err)
}

const createCommentDto = (rowId, colId, comment, commentStatus, reportId) => {
  return {
    headerRowId: rowId,
    headerColId: colId,
    comment: comment,
    commentStatus: commentStatus,
    reportSubmissionId: reportId
  }
}

export const actionCreators = {
  setReadOnly: (readOnly) => (dispatch, getState) => {
    dispatch({ type: actionTypes.ReportSubmissonTableIsReadOnly, newValue: readOnly })
  },

  get: (id, groupId) => (dispatch, getState) => {
    getInternal(dispatch, getState, id, groupId)
  },

  setCellComment: (colId, rowId, col, row, comment, commentStatus) => (dispatch, getState) => {
    const state = getState()

    if (!state.reportSubmissions || !state.reportSubmissions.reportSubmission) throw Error('No report has been loaded')

    const reportId = state.reportSubmissions.reportSubmission.id
    const dto = createCommentDto(rowId, colId, comment, commentStatus, reportId)

    dispatch({ type: actionTypes.ReportSubmissionSetComment, colId, rowId, col, row, comment, commentStatus })
    context.api.reportSubmissionService.editCellComment(dto)
  },

  getWithGroup: (entityId, parentId) => (dispatch, getState) => {
    dispatch({ type: actionTypes.ReportSubmissionWithGroupFetching })
    context.api.reportSubmissionService.getWithGroup(entityId, parentId)
      .then(response => {
        let finalizedGrid = null

        if (response.reportSubmission) {
          finalizedGrid = processUserReport(response.reportSubmission)
        }

        const sourceData = response.reportSubmission.reportSubmissionCells
        const cellMetadata = getCellMetadataFromReportSubmissionData(response.reportSubmission.reportSubmissionCells)

        dispatch({ type: actionTypes.ReportSubmissionWithGroupFetched, data: response })
        dispatch({ type: actionTypes.ReportSubmissionFetched, data: finalizedGrid })
        dispatch({ type: actionTypes.ReportSubmissionDataUpdated, data: { cellMetadata: cellMetadata, data: sourceData } })
      })
      .catch(() => dispatch({ type: actionTypes.ReportSubmissionError, update: { fetching: false, reportSubmissionWithGroup: null } }))
  },

  addAttachments: (input) => (dispatch, getState) => {
    dispatch({ type: actionTypes.ReportSubmissionAttachmentAdding })
    context.api.reportSubmissionService.addAttachments(input)
      .then(response => {
        dispatch({ type: actionTypes.ReportSubmissionAttachmentAdded })
      })
      .catch(() => dispatch({ type: actionTypes.ReportSubmissionError, update: { adding: false } }))
  },

  removeAttachments: (input) => (dispatch, getState) => {
    dispatch({ type: actionTypes.ReportSubmissionAttachmentRemoving })
    context.api.reportSubmissionService.removeAttachments(input)
      .then(response => {
        dispatch({ type: actionTypes.ReportSubmissionAttachmentRemoved })
      })
      .catch(() => dispatch({ type: actionTypes.ReportSubmissionError, update: { removing: false } }))
  },

  updateState: (input) => (dispatch, getState) => {
    const state = getState()

    let reportSubmissionId = null
    if (state.reportSubmissions.reportSubmission) reportSubmissionId = state.reportSubmissions.reportSubmission.id

    dispatch({ type: actionTypes.ReportSubmissionStatusUpdating })
    context.api.reportSubmissionService.updateState(input)
      .then(response => {
        dispatch({ type: actionTypes.ReportSubmissionStatusUpdated, data: input.state })

        getInternal(dispatch, getState, reportSubmissionId)
      })
      .catch(() => dispatch({ type: actionTypes.ReportSubmissionError }))
  },
  clearReportData: (readOnly) => (dispatch, getState) => {
    dispatch({ type: actionTypes.ReportSubmissonTableIsReadOnly, newValue: readOnly })
    dispatch({ type: actionTypes.ReportSubmissionFetching })

    const state = getState()

    context.api.reportSubmissionService.clearReportData(state.reportSubmissions.reportSubmission.id)
      .then(response => {
        getInternal(dispatch, getState, state.reportSubmissions.reportSubmission.id)
      })
  },
  prefillFromPrevious: () => (dispatch, getState) => {
    const state = getState()

    if (!state.reportSubmissions.reportSubmission) return

    const reportId = state.reportSubmissions.reportSubmission.id

    context.api.reportSubmissionService.prefillFromPrevious(reportId)
      .then(response => {
        getInternal(dispatch, getState, state.reportSubmissions.reportSubmission.id)
      })
      .catch(err => {
        const error = err && err.response && err.response.data && err.response.data.error
        openErrorDialog({ customTitle: 'Reporting.PrefillFromPreviousErrorTitle',
          customErrorText: error.message
        })
      })
  },
  createUserRow: (row) => (dispatch, getState) => {
    const state = getState()
    if (state.reportSubmissions.reportSubmission === null) throw Error('No report has been fetched')

    const reportSubmissionId = state.reportSubmissions.reportSubmission.id
    const reportInstanceGroupId = state.reportSubmissions.reportSubmission.reportInstanceGroupId

    dispatch({ type: actionTypes.ReportSubmissionFetching, data: reportSubmissionId })
    dispatch({ type: actionTypes.ReportSubmissionDataUpdating })
    dispatch({ type: actionTypes.UserRowsUpdating, data: row.row })

    const dto = {
      name: row.name,
      latinName: row.latinName,
      code: row.code,
      rowReportSubmissionId: reportSubmissionId,
      reportHeaderTemplateId: row.headerTemplateId,
      order: row.order,
      parentId: row.parentId === -1 ? null : row.parentId,
      definitionType: row.definitionType,
      reportDataType: row.reportDataType,
      fromDefinitionType: row.fromDefinitionType,
      reportInstanceGroupId
    }

    context.api.userRowHeaderService.createUserRowHeader(dto)
      .then(response => {
        dispatch({ type: actionTypes.UserRowsUpdated })
        unfinishedStateSet(getState, dispatch)
        receivedReport(response, dispatch)
      })
      .catch(() => dispatch({ type: actionTypes.ReportSubmissionError, update: { userRowsUpdating: false } }))
  },

  updateUserRow: (row) => (dispatch, getState) => {
    const state = getState()
    if (state.reportSubmissions.reportSubmission === null) throw Error('No report has been fetched')

    const reportSubmissionId = state.reportSubmissions.reportSubmission.id
    const reportInstanceGroupId = state.reportSubmissions.reportSubmission.reportInstanceGroupId

    dispatch({ type: actionTypes.UserRowsUpdating, data: row.row })

    context.api.userRowHeaderService.updateUserRowHeader({
      id: row.id,
      name: row.name,
      latinName: row.latinName,
      code: `[${row.name}]`,
      rowReportSubmissionId: reportSubmissionId,
      reportHeaderTemplateId: row.headerTemplateId,
      order: row.order,
      definitionType: row.definitionType,
      fromDefinitionType: row.fromDefinitionType,
      reportInstanceGroupId
    })
      .then(response => {
        dispatch({ type: actionTypes.UserRowsUpdated })
        getInternal(dispatch, getState, reportSubmissionId)
        unfinishedStateSet(getState, dispatch)
      })
      .catch(() => dispatch({ type: actionTypes.ReportSubmissionError, update: { userRowsUpdating: false } }))
  },

  deleteUserRow: (row, scrollToRow) => (dispatch, getState) => {
    const state = getState()
    if (state.reportSubmissions.reportSubmission === null) throw Error('No report has been fetched')

    const reportSubmissionId = state.reportSubmissions.reportSubmission.id
    const reportInstanceGroupId = state.reportSubmissions.reportSubmission.reportInstanceGroupId

    dispatch({ type: actionTypes.ReportSubmissionFetching, data: reportSubmissionId })
    dispatch({ type: actionTypes.ReportSubmissionDataUpdating })
    dispatch({ type: actionTypes.UserRowsUpdating, data: scrollToRow })

    context.api.userRowHeaderService.deleteUserRowHeader(row.id, reportInstanceGroupId, reportSubmissionId)
      .then(response => {
        dispatch({ type: actionTypes.UserRowsUpdated })
        unfinishedStateSet(getState, dispatch)
        receivedReport(response, dispatch)
      })
      .catch(() => dispatch({ type: actionTypes.ReportSubmissionError, update: { userRowsUpdating: false } }))
  }
}

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.ReportSubmissionSetComment: return { ...state, sourceData: updateCommentInSourceData(state.sourceData, action) }
    case actionTypes.ReportSubmissionFetching: {
      return { ...state, fetching: true, fetchingId: action.data }
    }
    case actionTypes.ReportSubmissionFetched: return { ...state,
      fetching: false,
      reloading: false,
      fetchingId: null,
      reportSubmission: action.data,
      sourceRowHeaders: action.data.allRows,
      sourceHeaders: action.data.allCols,
      reportSubmissionState: action.data.state,
      bankDefResidentCodes: action.data.bankDefResidentCodes,
      bankDefNonResidentCodes: action.data.bankDefNonResidentCodes,
      bankDefCodes: action.data.bankDefCodes,
      branchDefCodes: action.data.branchDefCodes,
      userDefCodes: action.data.userDefCodes,
      reportKey: state.reportKey + 1
    }
    case actionTypes.ReportSubmissonTableIsReadOnly: return { ...state, readOnly: action.newValue }
    case actionTypes.ReportSubmissionWithGroupFetching: return { ...state, fetching: true }
    case actionTypes.ReportSubmissionWithGroupFetched: return { ...state, fetching: false, reportSubmissionWithGroup: action.data }
    case actionTypes.ReportSubmissionAttachmentAdding: return { ...state, adding: true }
    case actionTypes.ReportSubmissionAttachmentAdded: return { ...state, adding: false, attachmentsKey: getComponentKey() }
    case actionTypes.ReportSubmissionAttachmentRemoving: return { ...state, removing: true }
    case actionTypes.ReportSubmissionAttachmentRemoved: return { ...state, removing: false, attachmentsKey: getComponentKey() }
    case actionTypes.ReportSubmissionStatusUpdating: return { ...state }
    case actionTypes.ReportSubmissionStatusUpdated: return { ...state, reportSubmissionState: action.data }
    case actionTypes.ReportSubmissionDataUpdating: return { ...state, sourceDataUpdating: true }
    case actionTypes.ReportSubmissionDataUpdated: {
      const newState = { ...state,
        sourceDataUpdating: false,
        scrollToRow: null
      }

      if (action.data.mergeData) {
        newState.sourceData = action.data.data ? mergeSourceData(state.sourceData, action.data.data) : state.sourceData
        newState.cellMetadata = action.data.cellMetadata ? mergeMetadata(state.cellMetadata, action.data.cellMetadata) : state.cellMetadata
      } else {
        newState.sourceData = action.data.data ? action.data.data : state.sourceData
        newState.cellMetadata = action.data.cellMetadata ? action.data.cellMetadata : state.cellMetadata
      }

      return newState
    }
    case actionTypes.ReportSubmissionGroupStatusUpdated: return {
      ...state,
      reportSubmission: { ...state.reportSubmission, reportSubmissionGroupState: action.data }
    }
    case actionTypes.ReportSubmissionError: return { ...state, ...action.update }
    case actionTypes.UserRowsUpdating: return { ...state, scrollToRow: action.data }
    case actionTypes.UserRowsUpdated: return { ...state, reportKey: ++state.reportKey }
    case actionTypes.UserRowsUpdate: return { ...state, sourceRowHeaders: action.data }
    case actionTypes.UserRowsCreate: return { ...state,
      fetching: false,
      sourceRowHeaders: action.data,
      sourceData: action.sourceData,
      cellMetadata: action.cellMetadata
    }

    case 'reportSubmission/instanceGroupStateChanged':
      if (!!state.reportSubmission &&
          action.data.reportInstanceGroupId === state.reportSubmission.reportInstanceGroupId &&
          action.data.reportInstanceGroupState !== state.reportSubmission.reportGroupProcessState) {
        const reportSubmission = { ...state.reportSubmission, reportGroupProcessState: action.data.reportInstanceGroupState }
        return { ...state, reportSubmission }
      }
      return state
    default: return state
  }
}

function updateCommentInSourceData (sourceData, { colId, rowId, col, row, comment, commentStatus }) {
  if (sourceData.find(x => x.headerColId === colId && x.headerRowId === rowId)) {
    return sourceData.map(x => {
      if (x.headerColId === colId && x.headerRowId === rowId) {
        return { ...x, comment: comment, commentStatus: commentStatus }
      }
      return x
    })
  }
  return [...sourceData, {
    headerColId: colId,
    headerRowId: rowId,
    columnTreeCode: col,
    rowTreeCode: row,
    comment: comment,
    commentStatus: commentStatus
  }]
}



// WEBPACK FOOTER //
// ./src/store/ReportSubmissions.js